<template>
    <div class="javadoc-page">
        <!-- ==================== 工具条 ==================== -->
        <div class="jd-toolbar">
            <div class="jd-toolbar__info">
                <span class="jd-toolbar__icon">
                    <el-icon>
                        <Memo />
                    </el-icon>
                </span>
                <span class="jd-toolbar__meta">
                    <span class="jd-toolbar__title">JavaDoc 项目</span>
                    <span class="jd-toolbar__subtitle">维护代码解析项目，配置文档与源码的可见范围，并支持脚本自动化上传更新</span>
                </span>
            </div>
            <div class="jd-toolbar__actions">
                <el-button class="jd-btn jd-btn--ghost" @click="openHelp()">
                    <el-icon class="jd-btn__icon">
                        <QuestionFilled />
                    </el-icon>
                    自动化配置说明
                </el-button>
                <el-button class="jd-btn jd-btn--ghost" :loading="loading" @click="loadProjectList">
                    <el-icon v-if="!loading" class="jd-btn__icon">
                        <Refresh />
                    </el-icon>
                    刷新
                </el-button>
                <el-button class="jd-btn jd-btn--ghost" @click="openUploadDialog">
                    <el-icon class="jd-btn__icon">
                        <Upload />
                    </el-icon>
                    上传zip
                </el-button>
                <el-button class="jd-btn" type="primary" @click="openCreateProject">
                    <el-icon class="jd-btn__icon">
                        <Plus />
                    </el-icon>
                    新建项目
                </el-button>
            </div>
        </div>

        <!-- ==================== 项目列表 ==================== -->
        <section class="jd-panel" v-loading="loading">
            <el-empty v-if="projectList.length === 0 && !loading" description="还没有项目，点击右上角「新建项目」开始" />
            <el-table v-else class="jd-table" :data="projectList" stripe style="width: 100%">
                <el-table-column prop="name" label="名称" min-width="200" show-overflow-tooltip />
                <el-table-column label="Token（自动化凭证）" min-width="250">
                    <template #default="scope">
                        <div class="jd-token">
                            <el-tooltip v-if="scope.row.token" :content="scope.row.token" placement="top">
                                <span class="jd-token__text">{{ scope.row.token }}</span>
                            </el-tooltip>
                            <span v-else class="jd-token__empty">—</span>
                            <el-link v-if="scope.row.token" class="jd-token__copy" type="primary"
                                @click="copyText(scope.row.token, 'Token 已复制')">复制</el-link>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="访问权限团队" min-width="180">
                    <template #default="scope">
                        <template v-if="scope.row.teamsReadList && scope.row.teamsReadList.length">
                            <el-tag v-for="item in scope.row.teamsReadList" :key="item.id" class="jd-tag" type="success"
                                size="small" effect="plain">{{ item.name }}</el-tag>
                        </template>
                        <el-tag v-else class="jd-tag" type="info" size="small" effect="plain">全部用户</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="源码权限团队" min-width="180">
                    <template #default="scope">
                        <template v-if="scope.row.teamsCodeList && scope.row.teamsCodeList.length">
                            <el-tag v-for="item in scope.row.teamsCodeList" :key="item.id" class="jd-tag" type="warning"
                                size="small" effect="plain">{{ item.name }}</el-tag>
                        </template>
                        <el-tag v-else class="jd-tag" type="info" size="small" effect="plain">不限制</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="createTime" label="创建时间" width="170" />
                <el-table-column prop="updateTime" label="更新时间" width="170" />
                <el-table-column label="操作" width="150" fixed="right">
                    <template #default="scope">
                        <el-link class="jd-op" type="primary" @click="openEditProject(scope.row)">编辑</el-link>
                        <el-link class="jd-op" type="warning" @click="openHelp(scope.row)">脚本</el-link>
                    </template>
                </el-table-column>
            </el-table>
        </section>
    </div>

    <!-- ==================== 新建 / 编辑项目 ==================== -->
    <el-dialog class="jd-dialog" :title="projectForm.mode == 'create' ? '新建项目' : '编辑项目'"
        v-model="projectForm.dialog" width="min(580px, 94vw)" :close-on-click-modal="false">
        <el-form :model="projectForm.prop" label-width="112px">
            <el-form-item label="项目名称">
                <el-input v-model="projectForm.prop.name" placeholder="例如 hicmp-common-hiutils" maxlength="64"
                    autocomplete="off" :disabled="projectForm.mode == 'edit'" />
                <p class="jd-tips" v-if="projectForm.mode == 'create'">需与自动化脚本的 name 参数、上传的 zip 包名保持一致</p>
                <p class="jd-tips" v-else>名称与自动化脚本、关联数据绑定，创建后不可修改</p>
            </el-form-item>
            <el-form-item label="描述">
                <el-input type="textarea" :rows="2" maxlength="200" placeholder="简要说明该项目的用途"
                    v-model="projectForm.prop.description" />
            </el-form-item>
            <el-form-item label="访问权限团队">
                <el-select v-model="projectForm.prop.teamsRead" multiple placeholder="可选择已加入的团队" style="width: 100%">
                    <el-option v-for="item in myJoinTeams" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
                <p class="jd-tips">控制文档的搜索与浏览权限，不选择时所有用户均可访问</p>
            </el-form-item>
            <el-form-item label="源码权限团队">
                <el-select v-model="projectForm.prop.teamsCode" multiple placeholder="可选择已加入的团队" style="width: 100%">
                    <el-option v-for="item in myJoinTeams" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
                <p class="jd-tips">控制源码查看权限，不选择时不限制源码查看</p>
            </el-form-item>
        </el-form>
        <template #footer>
            <div class="jd-dialog__foot">
                <el-button @click="projectForm.dialog = false">取 消</el-button>
                <el-button type="primary" :loading="savingProject" @click="saveProject">保 存</el-button>
            </div>
        </template>
    </el-dialog>

    <!-- ==================== 上传 zip ==================== -->
    <el-dialog class="jd-dialog" title="上传代码包" v-model="dialogUploadVisible" width="min(560px, 94vw)">
        <p class="jd-tips jd-tips--block">上传的 zip 包名将作为项目名称：若项目已存在则更新其内容，不存在则自动创建项目。</p>
        <java-doc-zip-upload :callback="uploadCallback" ref="javaDocZipUpload"></java-doc-zip-upload>
        <template #footer>
            <div class="jd-dialog__foot">
                <el-button type="primary" @click="dialogUploadVisible = false">完 成</el-button>
            </div>
        </template>
    </el-dialog>

    <!-- ==================== 自动化配置说明 ==================== -->
    <el-dialog class="jd-dialog jd-dialog--help" title="自动化上传配置说明" v-model="helpVisible"
        width="min(780px, 94vw)">
        <div class="jd-help">
            <p class="jd-help__lead">
                通过项目 Token 搭配配套脚本，即可让服务器定时拉取代码并提交解析，无需每次手动上传 zip。
            </p>

            <div class="jd-help__step">
                <span class="jd-help__no">1</span>
                <div class="jd-help__body">
                    <p class="jd-help__title">获取项目 Token</p>
                    <p class="jd-help__desc">每个项目拥有唯一 Token，在项目列表中点击「复制」即可获取。</p>
                    <div class="jd-help__kv" v-if="helpProject">
                        <span class="jd-help__kv-label">项目名称</span>
                        <span class="jd-help__kv-value">{{ helpProject.name }}</span>
                    </div>
                    <div class="jd-help__kv" v-if="helpProject">
                        <span class="jd-help__kv-label">项目 Token</span>
                        <span class="jd-help__kv-value">{{ helpProject.token || '—' }}</span>
                        <el-link v-if="helpProject.token" class="jd-help__copy" type="primary"
                            @click="copyText(helpProject.token, 'Token 已复制')">复制</el-link>
                    </div>
                </div>
            </div>

            <div class="jd-help__step">
                <span class="jd-help__no">2</span>
                <div class="jd-help__body">
                    <p class="jd-help__title">使用 job.sh 打包并提交代码</p>
                    <p class="jd-help__desc">脚本会把指定目录下的所有 .java 文件打包为 zip，并通过 Token 提交到 Hidoc。</p>
                    <div class="jd-code">
                        <pre>{{ jobUsageText }}</pre>
                        <el-link class="jd-code__copy" type="primary"
                            @click="copyText(jobUsageText, '命令已复制')">复制</el-link>
                    </div>
                    <el-collapse v-model="helpCollapse" class="jd-help__collapse">
                        <el-collapse-item name="script">
                            <template #title>查看 job.sh 完整脚本</template>
                            <div class="jd-code">
                                <pre>{{ jobScriptText }}</pre>
                                <el-link class="jd-code__copy" type="primary"
                                    @click="copyText(jobScriptText, '脚本已复制')">复制</el-link>
                            </div>
                        </el-collapse-item>
                    </el-collapse>
                </div>
            </div>

            <div class="jd-help__step">
                <span class="jd-help__no">3</span>
                <div class="jd-help__body">
                    <p class="jd-help__title">编排并配置定时执行</p>
                    <p class="jd-help__desc">参考 start.sh，把多个项目的提交命令写在一起，再用 crontab 定时执行。</p>
                    <div class="jd-code">
                        <pre>{{ startShText }}</pre>
                        <el-link class="jd-code__copy" type="primary"
                            @click="copyText(startShText, '脚本已复制')">复制</el-link>
                    </div>
                    <div class="jd-code">
                        <pre>{{ cronText }}</pre>
                        <el-link class="jd-code__copy" type="primary"
                            @click="copyText(cronText, '命令已复制')">复制</el-link>
                    </div>
                </div>
            </div>

            <el-alert class="jd-help__alert" type="warning" :closable="false" show-icon
                title="上传的 zip 包名（即脚本中的 name 参数）必须与项目名称完全一致，且 Token 正确，否则接口会拒绝该次提交。" />
        </div>
        <template #footer>
            <div class="jd-dialog__foot jd-dialog__foot--center">
                <el-button type="primary" @click="helpVisible = false">我知道了</el-button>
            </div>
        </template>
    </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus';
import request from '../../../utils/request.js';
import JavaDocZipUpload from '../components/JavaDocZipUpload';

// job.sh 完整脚本（与 05_PUB/hidoc-javadoc-sh/job.sh 保持一致）
const JOB_SCRIPT = [
    '#!/bin/bash',
    '# 用法：sh job.sh name=项目名称 path=源码目录 token=项目Token',
    'if [ $# -gt 0 ];then',
    '',
    '    echo "main: ----------开始----------"',
    '    openApiUrl="http://192.168.14.184/hidoc_server_api/openapi/javadoc/uploadZip/"',
    '    path=""',
    '    name=""',
    '    token=""',
    '',
    '    echo "main: ----------获取入参----------"',
    '    for param in "$@"',
    '    do',
    '        paramName=${param%%=*}',
    '        paramValue=${param#*=}',
    '',
    '        if [ "$paramName" = "name" ];then',
    '            name=$paramValue',
    '        elif [ "$paramName" = "path" ];then',
    '            path=$paramValue',
    '        elif [ "$paramName" = "token" ];then',
    '            token=$paramValue',
    '        fi',
    '    done',
    '',
    '    echo "main: name: $name"',
    '    echo "main: path: $path"',
    '    echo "main: token: $token"',
    '',
    '    echo "main: ----------创建工作区----------"',
    '    mkdir workspace',
    '    cd workspace',
    '',
    '    echo "main: ----------删除工程目录，重新生成空的目录----------"',
    '    rm -rf ./$name/',
    '    mkdir $name',
    '',
    '    echo "main: ----------删除zip包----------"',
    '    rm -rf ./$name.zip',
    '',
    '    echo "main: ----------从指定目录复制java文件到项目目录----------"',
    '    for file in `find $path -name *.java`',
    '    do',
    '        uuid=$(uuidgen |sed \'s/-//g\')',
    '        cp $file ./$name/$uuid.java',
    '    done',
    '',
    '    echo "main: ----------打包zip文件----------"',
    '    zip -q -r $name.zip ./$name',
    '',
    '    echo "main: ----------提交到hidoc接口中----------"',
    '    curl -F "file=@$name.zip" -X POST $openApiUrl$token',
    '',
    '    echo "main: ----------任务执行完成----------"',
    '',
    'else',
    '    echo "main: ----------异常：参数为空，终止任务----------"',
    'fi'
].join('\n');

export default {
    data() {
        return {
            loading: false,
            savingProject: false,
            projectList: [],
            myJoinTeams: [],
            projectForm: {
                mode: 'create',
                dialog: false,
                prop: {
                    id: '',
                    name: '',
                    description: '',
                    teamsRead: [],
                    teamsCode: []
                }
            },
            dialogUploadVisible: false,
            helpVisible: false,
            helpProject: null,
            helpCollapse: [],
            jobScriptText: JOB_SCRIPT
        };
    },
    components: { JavaDocZipUpload },
    computed: {
        helpName() {
            return this.helpProject ? this.helpProject.name : 'your-project-name';
        },
        helpToken() {
            return this.helpProject && this.helpProject.token ? this.helpProject.token : 'your-project-token';
        },
        jobUsageText() {
            return 'sh job.sh name=' + this.helpName + ' path=/data/git/' + this.helpName + ' token=' + this.helpToken;
        },
        startShText() {
            return [
                '# start.sh 示例：一次更新多个项目',
                this.jobUsageText,
                'sh job.sh name=hicmp-sale path=/data/git/hicmp/hicmp-sale token=xxxxxxxxxxxxxxxx'
            ].join('\n');
        },
        cronText() {
            return [
                '# 每天凌晨 2 点自动更新所有项目',
                '0 2 * * * sh /data/git/hidoc-javadoc-sh/start.sh'
            ].join('\n');
        }
    },
    mounted() {
        this.loadProjectList();
    },
    methods: {
        loadProjectList() {
            this.loading = true;
            return request({
                url: '/javadoc/projectList',
                method: 'post',
                data: { token: '' }
            }).then(res => {
                if (res.code == 0) {
                    this.projectList = res.data || [];
                }
            }).catch(() => { }).finally(() => {
                this.loading = false;
            });
        },
        loadMyJoinTeams() {
            return request({
                url: '/team/getMyJoinTeams',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0) {
                    this.myJoinTeams = res.data || [];
                }
            }).catch(() => { });
        },
        // 打开上传文件窗口
        openUploadDialog() {
            this.dialogUploadVisible = true;
            this.$nextTick(() => {
                const uploadPanel = this.$refs['javaDocZipUpload'];
                if (uploadPanel) uploadPanel.openPanel();
            });
        },
        uploadCallback(data) {
            if (data.response.code == 0) {
                this.dialogUploadVisible = false;
                this.$message.success(data.response.msg);
            } else {
                this.$message.error(data.response.msg);
            }
            this.loadProjectList();
        },
        openCreateProject() {
            this.loadMyJoinTeams();
            this.projectForm.prop = {
                id: '',
                name: '',
                description: '',
                teamsRead: [],
                teamsCode: []
            };
            this.projectForm.mode = 'create';
            this.projectForm.dialog = true;
        },
        openEditProject(row) {
            this.loadMyJoinTeams();
            this.projectForm.prop = {
                id: row.id,
                name: row.name,
                description: row.description || '',
                teamsRead: (row.teamsReadList || []).map(item => item.id),
                teamsCode: (row.teamsCodeList || []).map(item => item.id)
            };
            this.projectForm.mode = 'edit';
            this.projectForm.dialog = true;
        },
        saveProject() {
            const prop = this.projectForm.prop;
            const isCreate = this.projectForm.mode == 'create';
            const name = (prop.name || '').trim();
            if (isCreate && !name) {
                ElMessage.warning('请填写项目名称');
                return;
            }
            // 项目名称不可修改，编辑时不提交名称
            const data = {
                description: prop.description,
                teamsRead: prop.teamsRead,
                teamsCode: prop.teamsCode
            };
            this.savingProject = true;
            return request({
                url: isCreate ? '/javadoc/projectCreate' : '/javadoc/projectEdit',
                method: 'post',
                data: isCreate ? Object.assign({ name: name }, data) : Object.assign({ id: prop.id }, data)
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '保存成功', type: 'success', duration: 2 * 1000 });
                    this.projectForm.dialog = false;
                    this.loadProjectList();
                }
            }).catch(() => { }).finally(() => {
                this.savingProject = false;
            });
        },
        openHelp(project) {
            this.helpProject = project || null;
            this.helpCollapse = [];
            this.helpVisible = true;
        },
        copyText(text, tip) {
            if (!text) return;
            const success = () => ElMessage({ message: tip || '已复制', type: 'success', duration: 1500 });
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(success).catch(() => this.copyByTextarea(text, success));
            } else {
                this.copyByTextarea(text, success);
            }
        },
        copyByTextarea(text, success) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
                success();
            } catch (e) {
                ElMessage.error('复制失败，请手动复制');
            }
            document.body.removeChild(textarea);
        }
    }
};
</script>

<style scoped>
.javadoc-page {
    /* 局部覆盖主色，与其它工作台页面保持同一套视觉语言 */
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --jd-text: #1f2d3d;
    --jd-sub: #5b6a80;
    --jd-muted: #aab4c4;
    --jd-border: #e6eaf0;

    color: var(--jd-text);
}

/* ---------- 工具条 ---------- */
.jd-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    row-gap: 10px;
    padding: 10px 16px;
    border: 1px solid #ebeef5;
    border-radius: 12px;
    background: linear-gradient(180deg, #ffffff 0%, #fafbfd 100%);
    box-shadow: 0 10px 28px -22px rgba(31, 45, 61, 0.55);
}

.jd-toolbar__info {
    display: flex;
    align-items: center;
    min-width: 0;
}

.jd-toolbar__icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin-right: 10px;
    border-radius: 10px;
    font-size: 18px;
    color: #fff;
    background: linear-gradient(135deg, #6a9cff 0%, #2f6fed 100%);
    box-shadow: 0 8px 16px -10px rgba(47, 111, 237, 0.9);
}

.jd-toolbar__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.jd-toolbar__title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: var(--jd-text);
}

.jd-toolbar__subtitle {
    font-size: 12px;
    line-height: 16px;
    color: #a8abb2;
}

.jd-toolbar__actions {
    display: flex;
    flex: none;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
}

/* ---------- 按钮 ---------- */
.jd-btn {
    height: 34px;
    padding: 0 16px;
    border-radius: 9px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.jd-btn--ghost {
    color: #606266;
    background-color: #fff;
    border-color: #e4e7ed;
}

.jd-btn--ghost:hover,
.jd-btn--ghost:focus {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
    border-color: var(--el-color-primary-light-5);
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -12px rgba(47, 111, 237, 0.9);
}

.jd-btn__icon {
    margin-right: 4px;
}

/* ---------- 面板容器 ---------- */
.jd-panel {
    margin-top: 12px;
    padding: 14px 16px;
    border: 1px solid var(--jd-border);
    border-radius: 14px;
    background-color: #fff;
    box-shadow: 0 1px 2px rgba(28, 47, 84, 0.05);
}

.jd-panel :deep(.el-empty) {
    padding: 30px 0;
}

/* ---------- 表格 ---------- */
.jd-panel :deep(.jd-table) {
    --el-table-header-bg-color: #f7f9fc;
    font-size: 13px;
}

.jd-panel :deep(.jd-table th.el-table__cell) {
    color: var(--jd-muted);
    font-weight: 600;
}

.jd-panel :deep(.jd-table .el-table__cell) {
    padding: 9px 0;
}

/* ---------- Token ---------- */
.jd-token {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.jd-token__text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    color: var(--jd-sub);
    text-overflow: ellipsis;
    white-space: nowrap;
}

.jd-token__empty {
    color: var(--jd-muted);
}

.jd-token__copy {
    flex: none;
    font-size: 12px;
}

.jd-tag {
    margin: 2px 6px 2px 0;
}

/* ---------- 行内操作 ---------- */
.jd-op {
    margin-right: 14px;
    font-size: 13px;
}

.jd-op:last-child {
    margin-right: 0;
}

/* ---------- 小尺寸设备适配 ---------- */
@media (max-width: 768px) {
    .jd-toolbar {
        padding: 10px 12px;
    }

    .jd-toolbar__subtitle {
        display: none;
    }

    .jd-toolbar__actions {
        flex: 1 1 100%;
    }

    .jd-toolbar__actions .jd-btn {
        flex: 1 1 0;
    }

    .jd-panel {
        padding: 12px;
    }
}
</style>

<!-- 弹窗会 teleport 到 body，样式需放在非 scoped 块并带唯一前缀 -->
<style>
.jd-dialog {
    /* 主色令牌需在此再声明一遍，弹窗取不到页面作用域 */
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    max-width: calc(100vw - 32px);
    border-radius: 14px;
    box-shadow: 0 18px 50px -12px rgba(28, 47, 84, 0.22);
}

.jd-dialog .el-dialog__header {
    margin-right: 0;
    padding: 22px 24px 0;
}

.jd-dialog .el-dialog__title {
    font-size: 17px;
    font-weight: 600;
    color: #1f2d3d;
}

.jd-dialog .el-dialog__headerbtn {
    top: 14px;
    right: 14px;
}

.jd-dialog .el-dialog__body {
    padding: 18px 24px 4px;
}

.jd-dialog .el-dialog__footer {
    padding: 0 24px 22px;
}

.jd-dialog .el-form-item {
    margin-bottom: 18px;
}

.jd-dialog .el-form-item__label {
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
    color: #8a94a6;
}

.jd-dialog .jd-tips {
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 16px;
    color: #aab4c4;
}

.jd-dialog .jd-tips--block {
    margin: 0 0 10px;
}

.jd-dialog__foot {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.jd-dialog__foot .el-button {
    min-width: 96px;
    height: 38px;
    margin-left: 0;
    border-radius: 10px;
}

.jd-dialog__foot--center {
    justify-content: center;
}

.jd-dialog__foot--center .el-button {
    min-width: 200px;
}

/* ---------- 自动化配置说明 ---------- */
.jd-help__lead {
    margin: 0 0 16px;
    font-size: 13px;
    line-height: 20px;
    color: #5b6a80;
}

.jd-help__step {
    display: flex;
    gap: 12px;
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px dashed #eef1f6;
}

.jd-help__step:last-of-type {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: 0;
}

.jd-help__no {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(135deg, #6a9cff 0%, #2f6fed 100%);
}

.jd-help__body {
    flex: 1 1 auto;
    min-width: 0;
}

.jd-help__title {
    margin: 0 0 6px;
    font-size: 14px;
    font-weight: 600;
    color: #1f2d3d;
}

.jd-help__desc {
    margin: 0 0 10px;
    font-size: 13px;
    line-height: 20px;
    color: #5b6a80;
}

.jd-help__kv {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
    font-size: 13px;
}

.jd-help__kv-label {
    flex: none;
    width: 72px;
    color: #8a94a6;
}

.jd-help__kv-value {
    overflow: hidden;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    color: #1f2d3d;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.jd-help__copy {
    flex: none;
    font-size: 12px;
}

.jd-code {
    position: relative;
    margin-bottom: 10px;
    border: 1px solid #e6eaf0;
    border-radius: 10px;
    background-color: #f7f9fc;
}

.jd-code pre {
    max-height: 320px;
    margin: 0;
    padding: 12px 64px 12px 14px;
    overflow: auto;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 18px;
    color: #33415c;
    white-space: pre;
}

.jd-code__copy {
    position: absolute;
    top: 8px;
    right: 10px;
    font-size: 12px;
}

.jd-help__collapse {
    border-top: 0;
}

.jd-help__collapse .el-collapse-item__header {
    height: 34px;
    border-bottom: 0;
    font-size: 13px;
    color: #5b6a80;
}

.jd-help__collapse .el-collapse-item__wrap {
    border-bottom: 0;
}

.jd-help__collapse .el-collapse-item__content {
    padding-bottom: 0;
}

.jd-help__alert {
    margin-top: 16px;
    border-radius: 10px;
}

@media (max-width: 768px) {
    .jd-dialog .el-dialog__header {
        padding: 16px 16px 0;
    }

    .jd-dialog .el-dialog__body {
        padding: 14px 14px 4px;
    }

    .jd-dialog .el-dialog__footer {
        padding: 0 14px 16px;
    }

    .jd-dialog__foot .el-button {
        flex: 1 1 0;
    }

    .jd-code pre {
        max-height: 220px;
    }
}
</style>
